import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { startOfMonth } from "date-fns";
import { useState } from "react";
import { AcLogo } from "@/components/marketing/ac-logo";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { orpcPublic } from "@/lib/orpc";
import { PublicActivityMonth, publicMonthRange } from "@/routes/-components/public-activity-month";

export const Route = createFileRoute("/activities/$token")({
  validateSearch: (search: Record<string, unknown>): { embed?: string } => ({
    embed: typeof search.embed === "string" ? search.embed : undefined,
  }),
  component: PublicActivities,
  head: () => ({
    meta: [
      { title: "Activity calendar" },
      { name: "robots", content: "noindex, nofollow" },
      // The token is in the address. Nothing this page links to may learn it.
      { name: "referrer", content: "no-referrer" },
    ],
  }),
});

/** The building's activity calendar for families and its website: read with the token in the link,
 * no account. `?embed=1` drops the chrome for an iframe. */
function PublicActivities() {
  const { token } = Route.useParams();
  const { embed } = Route.useSearch();
  const [monthStart, setMonthStart] = useState(() => startOfMonth(new Date()));
  const calendar = useQuery({
    ...orpcPublic.care.activityCalendar.share.public.queryOptions({
      input: { token, ...publicMonthRange(monthStart) },
    }),
    retry: false,
  });
  const facility = calendar.data?.facility;
  const zone = facility?.timezone ?? "America/New_York";

  return (
    <main className={embed ? "px-2 py-2" : "min-h-dvh px-4 py-6 sm:py-8"}>
      <div className="mx-auto flex w-full max-w-5xl flex-col gap-4">
        {embed ? null : (
          <div className="flex items-center justify-between gap-3 print:hidden">
            <AcLogo />
            <div className="flex gap-2">
              {calendar.data?.feedUrl ? (
                <Button asChild variant="outline" size="sm">
                  <a href={calendar.data.feedUrl}>Add to my calendar</a>
                </Button>
              ) : null}
              <Button variant="outline" size="sm" onClick={() => window.print()}>
                Print
              </Button>
            </div>
          </div>
        )}
        {calendar.isError ? (
          <section className="rounded-md border p-6 text-center">
            <h1 className="text-lg font-semibold">This calendar is not available</h1>
            <p className="text-muted-foreground mt-1 text-sm">
              The link may have been replaced or switched off. Ask the community for a new one.
            </p>
          </section>
        ) : (
          <>
            <header>
              <h1 className="text-2xl font-semibold">
                {facility ? `${facility.name} activities` : <Skeleton className="h-7 w-64" />}
              </h1>
              {facility ? (
                <p className="text-muted-foreground text-sm">
                  {[facility.city, facility.state].filter(Boolean).join(", ")}
                  {facility.phone ? `, ${facility.phone}` : ""}
                </p>
              ) : null}
            </header>
            <PublicActivityMonth
              monthStart={monthStart}
              setMonthStart={setMonthStart}
              rows={calendar.data?.rows ?? []}
              zone={zone}
              pending={calendar.isPending || calendar.isFetching}
            />
          </>
        )}
      </div>
    </main>
  );
}
